import { serverNow } from "@storyboard/web-client";
import { createLogger } from "@/logger";
import { cursorFor, registryCursorStore, type SyncCursorStore } from "@/models/sync/cursorStore";
import type { SyncSignals, SyncTable } from "@/models/sync/signals";
import { applySyncDelta, emptySyncDelta, type SyncDelta, type SyncExtras } from "@/models/sync/syncDelta";
import { type CursorSyncTarget, type SyncContext, type SyncTarget, syncTargets } from "@/models/sync/syncTargets";
import { flightRecorder } from "@/services/debug/FlightRecorder";

export { SYNC_CURSOR_OVERLAP_MS as SYNC_CURSOR_SKEW_BUFFER_MS, type SyncCursorStore } from "@/models/sync/cursorStore";

const log = createLogger("appsync");

/** Page-walk circuit breaker; a walk that trips it leaves its cursor where it was so the next run retries the window. */
export const SYNC_MAX_PAGES = 50;
/** A failed walk is retried this many times, each after `SYNC_RETRY_DELAY_MS` times the attempt number. */
export const SYNC_MAX_ATTEMPTS = 3;
export const SYNC_RETRY_DELAY_MS = 5_000;

export type SyncSchedulerDeps = {
  targets: readonly SyncTarget[];
  store: SyncCursorStore;
  /** The server's clock; cursors are stamped in its timeline, never the browser's. */
  now: () => Date;
  applyDelta: typeof applySyncDelta;
  /** When this returns false the run was superseded (workspace switch) and must neither apply nor advance. */
  isStillActive?: (workspaceId: string) => boolean;
  maxPages?: number;
  /** Base delay before a failed walk is retried; defaults to {@link SYNC_RETRY_DELAY_MS}. */
  retryDelayMs?: number;
};

type TableState = {
  dirty: boolean;
  inFlight: Promise<void> | null;
  ctx: SyncContext | null;
  seed: string | undefined;
  eventItemIds: Set<string>;
  contentIds: Set<string>;
  /** Consecutive failed walks; reset by a walk that completes. */
  attempts: number;
  retryTimer: ReturnType<typeof setTimeout> | null;
};

function newTableState(): TableState {
  return {
    dirty: false,
    inFlight: null,
    ctx: null,
    seed: undefined,
    eventItemIds: new Set(),
    contentIds: new Set(),
    attempts: 0,
    retryTimer: null,
  };
}

function earlierOf(a: string | undefined, b: string): string {
  return a === undefined || new Date(b).getTime() < new Date(a).getTime() ? b : a;
}

/**
 * Turns AppSync signals into one fetch per dirty table, single-flight with a dirty re-run.
 *
 * A table marked dirty while its walk is in flight is walked again once that walk lands, so the last signal is always
 * followed by a fetch that started after it. A walk that throws keeps its signal: the table is re-marked dirty and
 * flushed again after a delay that grows with each attempt, up to `SYNC_MAX_ATTEMPTS`, so a transient failure heals
 * without waiting for another payload. Cursors are written as the server time the walk started minus the overlap,
 * advanced only after the whole window walked and only while the workspace is still the active one. A missing cursor
 * is seeded from the table's bootstrap cursor, else the earliest `updatedAt` the payload carried, else now minus the
 * overlap — this layer never walks a table unbounded; bootstrap owns full loads.
 */
export class SyncScheduler {
  private readonly targets: Map<SyncTable, SyncTarget>;
  private readonly states = new Map<SyncTable, TableState>();

  constructor(private readonly deps: SyncSchedulerDeps) {
    this.targets = new Map(deps.targets.map((target) => [target.table, target]));
  }

  /** Records the signals against their tables; nothing is fetched until {@link flush}. */
  public markDirty(signals: SyncSignals, ctx: SyncContext): void {
    for (const table of signals.tables) {
      if (!this.targets.has(table)) continue;
      const state = this.stateFor(table);
      state.dirty = true;
      state.ctx = ctx;
      const seed = signals.seeds.get(table);
      if (seed) state.seed = earlierOf(state.seed, seed);
    }
    // Per-item reads ride on the items walk so content and receipts land in the same post-fetch pass as the rows.
    if (signals.eventItemIds.size > 0 || signals.contentIds.size > 0) {
      const state = this.stateFor("items");
      state.dirty = true;
      state.ctx = ctx;
      for (const id of signals.eventItemIds) state.eventItemIds.add(id);
      for (const id of signals.contentIds) state.contentIds.add(id);
    }
  }

  /**
   * Marks every cursor table dirty and flushes, for the moments a gap may have opened: a resubscribe, the tab coming
   * back into view, the browser coming back online. A quiet workspace answers each walk with an empty page. Refetch
   * tables that own their own cursor are re-walked on the same terms; whole-set refetches wait for their signal.
   *
   * @param contentIds content whose parents get a delta content fetch — the recently created items whose artifacts may
   *   have landed while no signal could reach the tab.
   */
  public async resyncAll(ctx: SyncContext, contentIds: Iterable<string> = []): Promise<void> {
    const tables = new Set<SyncTable>();
    for (const target of this.targets.values()) {
      if (target.kind === "cursor" || target.ownsCursor) tables.add(target.table);
    }
    this.markDirty({ tables, seeds: new Map(), eventItemIds: new Set(), contentIds: new Set(contentIds) }, ctx);
    await this.flush();
  }

  /** Runs every dirty table not already in flight and resolves once the runs it started have landed. */
  public async flush(): Promise<void> {
    const started: Promise<void>[] = [];
    for (const [table, state] of this.states) {
      if (!state.dirty || state.inFlight) continue;
      started.push(this.runUntilClean(table, state));
    }
    await Promise.all(started);
  }

  private scheduleRetry(state: TableState): void {
    if (state.retryTimer) return;
    const delay = (this.deps.retryDelayMs ?? SYNC_RETRY_DELAY_MS) * state.attempts;
    state.retryTimer = setTimeout(() => {
      state.retryTimer = null;
      void this.flush();
    }, delay);
  }

  private stateFor(table: SyncTable): TableState {
    const existing = this.states.get(table);
    if (existing) return existing;
    const created = newTableState();
    this.states.set(table, created);
    return created;
  }

  private async runUntilClean(table: SyncTable, state: TableState): Promise<void> {
    const run = (async () => {
      while (state.dirty && state.ctx) {
        state.dirty = false;
        const ctx = state.ctx;
        const seed = state.seed;
        const eventItemIds = state.eventItemIds;
        const contentIds = state.contentIds;
        state.seed = undefined;
        state.eventItemIds = new Set();
        state.contentIds = new Set();
        try {
          await this.runOnce(table, ctx, seed, { eventItemIds, contentIds });
          state.attempts = 0;
        } catch (error) {
          state.attempts += 1;
          log.warn("[sync] Table sync failed; cursor left in place", {
            table,
            workspaceId: ctx.workspaceId,
            attempt: state.attempts,
            error,
          });
          if (state.attempts >= SYNC_MAX_ATTEMPTS) {
            state.attempts = 0;
            break;
          }
          // Put the signal back, merged with anything that arrived meanwhile, and try again after a delay rather than
          // spinning on a failing route.
          state.dirty = true;
          if (seed) state.seed = earlierOf(state.seed, seed);
          for (const id of eventItemIds) state.eventItemIds.add(id);
          for (const id of contentIds) state.contentIds.add(id);
          this.scheduleRetry(state);
          break;
        }
      }
    })();
    state.inFlight = run;
    try {
      await run;
    } finally {
      state.inFlight = null;
    }
  }

  private async runOnce(
    table: SyncTable,
    ctx: SyncContext,
    seed: string | undefined,
    extras: SyncExtras,
  ): Promise<void> {
    const target = this.targets.get(table);
    if (!target) return;
    if (!this.stillActive(ctx.workspaceId)) return;

    if (target.kind === "refetch") {
      await target.refetch(ctx);
      return;
    }

    const requestStart = this.deps.now();
    const timestamp = await this.resolveCursor(target, ctx.workspaceId, seed, requestStart);
    if (!this.stillActive(ctx.workspaceId)) return;

    const delta = emptySyncDelta();
    const walkedWholeWindow = await this.walkPages(target, ctx, timestamp, delta);
    if (!this.stillActive(ctx.workspaceId)) return;

    // Pages that landed get their content and side effects even when the breaker cut the walk short; only the cursor
    // waits for a whole window, so the unfetched remainder stays inside the next run.
    if (walkedWholeWindow) {
      await this.deps.store.setEventDate(target.cursorKey, ctx.workspaceId, cursorFor(requestStart));
    }
    flightRecorder.note(`sync:${table}:delta`);
    await this.deps.applyDelta(delta, ctx, extras);
  }

  /**
   * Walks the target's window a page at a time, applying each page as it lands.
   *
   * Returns false when the walk did not reach the end of the window — superseded, or the circuit breaker tripped —
   * so the caller leaves the cursor where it is and the next run retries the same window.
   */
  private async walkPages(
    target: CursorSyncTarget,
    ctx: SyncContext,
    timestamp: string,
    delta: SyncDelta,
  ): Promise<boolean> {
    const maxPages = this.deps.maxPages ?? SYNC_MAX_PAGES;
    for (let page = 0; page < maxPages; page++) {
      const { records, pageSize = target.pageSize } = await target.fetchPage({
        ...ctx,
        page,
        pageSize: target.pageSize,
        timestamp,
      });
      if (!this.stillActive(ctx.workspaceId)) return false;
      if (records.length > 0) {
        flightRecorder.note(`sync:${target.table}`);
        await target.apply(records, delta);
      }
      if (records.length < Math.min(pageSize, target.pageSize)) return true;
    }
    log.warn("[sync] Hit max page limit; leaving cursor for retry", { table: target.table, maxPages });
    return false;
  }

  private async resolveCursor(
    target: CursorSyncTarget,
    workspaceId: string,
    seed: string | undefined,
    requestStart: Date,
  ): Promise<string> {
    const cursor = await this.readCursor(target.cursorKey, workspaceId);
    if (cursor) return cursor.toISOString();
    const bootstrapCursor = target.bootstrapEvent
      ? await this.readCursor(target.bootstrapEvent, workspaceId)
      : undefined;
    if (bootstrapCursor) return cursorFor(bootstrapCursor).toISOString();
    if (seed) return cursorFor(new Date(seed)).toISOString();
    return cursorFor(requestStart).toISOString();
  }

  /** Reads a cursor, treating an unreadable store as "no cursor" so a registry hiccup degrades to a seeded window. */
  private async readCursor(event: string, workspaceId: string): Promise<Date | undefined> {
    try {
      return await this.deps.store.getEventDate(event, workspaceId);
    } catch (error) {
      log.warn("[sync] Cursor read failed", { event, workspaceId, error });
      return undefined;
    }
  }

  private stillActive(workspaceId: string): boolean {
    return this.deps.isStillActive?.(workspaceId) ?? true;
  }
}

let activeSyncWorkspaceId: string | null = null;

/** Names the workspace whose signals may apply; a run for any other workspace neither writes nor advances a cursor. */
export function setActiveSyncWorkspace(workspaceId: string | null): void {
  activeSyncWorkspaceId = workspaceId;
}

export const syncScheduler = new SyncScheduler({
  targets: syncTargets,
  store: registryCursorStore,
  now: serverNow,
  applyDelta: applySyncDelta,
  isStillActive: (workspaceId) => workspaceId === activeSyncWorkspaceId,
});
